// styled.js
import styled from 'styled-components';
import { Select } from 'antd';

/**
 * Helper for border radius from theme
 */
export const getBorderRadius = (theme) => {
  if (theme?.card_style?.includes('slightly-rounded')) return '8px';
  if (theme?.card_style?.includes('squared')) return '0';
  return '8px';
};

/**
 * 1) Container for the entire engagement section
 */
export const EngagementWrapper = styled.div`
  .engagement-title {
    font-size: 1.5rem;
    margin-bottom: 5px;
    color: ${({ theme }) => theme?.card_text_color || '#000'};
  }
  .engagement-description {
    font-size: 1rem;
    margin-bottom: 15px;
    color: ${({ theme }) => theme?.card_text_color || '#666'};
  }
`;

/**
 * 2) Horizontal scroll container for features
 */
export const FeaturesScrollContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 5px;
  overflow-x: scroll;
  padding-bottom: 16px;
  margin-bottom: 20px;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;

  /* Force scrollbar to be visible on WebKit browsers */
  &::-webkit-scrollbar {
    -webkit-appearance: none;
    height: 6px;
    background-color: ${({ theme }) => theme?.card_color || 'transparent'};
    display: block;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme?.card_color || '#1a1a1a'};
    border-radius: 3px;
    &:hover {
      background-color: ${({ theme }) => theme?.card_color || '#333'};
    }
  }

  /* For IE & Edge */
  -ms-overflow-style: none;
  /* For Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme?.card_color || '#1a1a1a'} transparent;

  flex-wrap: nowrap;
  -webkit-overflow-scrolling: touch;
`;

/**
 * 3) Individual feature card
 */
export const FeatureCard = styled.div`
  flex: 0 0 400px;
  max-width: 350px;
  border-radius: ${props => getBorderRadius(props.theme)};
  background: ${props =>
    props.theme?.card_style?.includes('outline')
      ? 'transparent'
      : props.theme?.card_color || '#1a1a1a'
  };
  border: ${props =>
    props.theme?.card_style?.includes('outline')
      ? `2px solid ${props.theme?.card_color || '#000'}`
      : 'none'
  };
  box-shadow: ${props =>
    props.theme?.card_style?.includes('shadow')
      ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      : 'none'
  };
  overflow: hidden;
  height: 300px;
  display: flex;
  flex-direction: row;
`;

/**
 * 4) Container for text & button
 */
export const FeatureContent = styled.div`
  flex: 1;
  width: ${props => (props.hasImage ? '60%' : '100%')};
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  > div:first-child {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
`;

/**
 * 5) Container for image on the right side
 */
export const FeatureImage = styled.div`
  width: 50%;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

/**
 * 6) Feature Title
 */
export const FeatureTitle = styled.h3`
  font-size: 18px;
  margin: 0;
  color: ${props => props.theme?.card_text_color || 'white'};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const ThemedSelect = styled(Select)`
   width: 100%;

   .ant-select-selector {
     background-color: ${({ theme }) => theme?.card_color || '#fff'};
     border: 1px solid ${({ theme }) => theme?.card_color || '#d9d9d9'};
     color: ${({ theme }) => theme?.text_color || '#000'};
     border-radius: ${({ theme }) => getBorderRadius(theme)};
   }

   .ant-select-arrow {
     color: ${({ theme }) => theme?.text_color || '#000'};
   }

   /* The dropdown menu itself */
   .ant-select-dropdown {
     background-color: ${({ theme }) => theme?.card_color || '#fff'};
     border-radius: ${({ theme }) => getBorderRadius(theme)};
   }

   /* Dropdown items */
   .ant-select-item {
     color: ${({ theme }) => theme?.text_color || '#000'};
   }

   /* Selected item highlight */
   .ant-select-item-option-selected {
     background-color: ${({ theme }) => theme?.button_color || '#ff385c'};
     color: ${({ theme }) => theme?.button_text_color || '#fff'};
   }
 `;

/**
 * 7) Feature Description
 */
export const FeatureDescription = styled.p`
  font-size: 14px;
  color: ${props => props.theme?.card_text_color || '#e0e0e0'};
  margin: 0;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
`;

/**
 * 8) Feature Price
 */
export const FeaturePrice = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: ${props => props.theme?.card_text_color || 'white'};
  margin: 12px 0;
`;

/**
 * 9) Book Now Button
 */
export const BookNowButton = styled.button`
  width: 100%;
  padding: 12px;
  background: ${props => props.theme?.button_color || '#ff385c'};
  color: ${props => props.theme?.button_text_color || 'white'};
  border: none;
  border-radius: ${props => getBorderRadius(props.theme?.button_style)};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;
